import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  Chip,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import Card from './Card';
import Table from './Table';
import Button from './Button';
import Alert from './Alert';
import Loading from './Loading';
import EmptyState from './EmptyState';
import api from '../services/api';

// Row `type` values from POST /api/skills/csv/preview, mirroring the
// healing agent's ResolveResult discriminant plus a local-only "invalid"
// for a shape error this app catches before ever calling the agent.
const TYPE_LABELS = {
  will_create: 'Will create',
  will_inherit: 'Will inherit',
  already_onboarded: 'Already onboarded',
  error: 'Error',
  invalid: 'Invalid',
  // Retailer has no screenshot/archive URL in Databricks (API-based
  // crawling) — DOM-based healing can never apply, so this app never
  // offers it for approval regardless of what the agent's own preview says.
  unhealable: 'Unhealable (API crawl)',
};

const TYPE_COLORS = {
  will_create: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  will_inherit: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  already_onboarded: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
  error: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  invalid: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  unhealable: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
};

// Rows in these states are the only ones worth submitting for approval —
// already-onboarded rows have nothing left to do, and error/invalid rows
// have nothing the queue could act on.
const APPROVABLE_TYPES = new Set(['will_create', 'will_inherit']);

// Statuses (from /csv/status, i.e. the agent's own skill row `status`)
// considered done — a poller stops once every submitted row reaches one.
const TERMINAL_STATUSES = new Set(['generated', 'edited', 'error']);

const STATUS_POLL_INTERVAL_MS = 3000;

// Example rows only — page_type covers all four accepted values so users
// see the shape without this being mistaken for a required exact set of rows.
const TEMPLATE_CSV_ROWS = [
  ['retailer', 'page_type', 'attribute'],
  ['ExampleRetailer', 'detail', 'extract.price'],
  ['ExampleRetailer', 'search', 'extract.title'],
  ['ExampleRetailer', 'category', 'extract.brand'],
  ['ExampleRetailer', 'store', 'extract.imageUrl'],
];

function downloadSkillCsvTemplate() {
  const csvText = TEMPLATE_CSV_ROWS.map((row) => row.join(',')).join('\n');
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'skill-onboarding-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function TypeChip({ type }) {
  return (
    <Chip
      label={TYPE_LABELS[type] || type}
      size="small"
      sx={{
        background: TYPE_COLORS[type] || 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
        color: 'white',
        fontWeight: 600,
      }}
    />
  );
}

function rowKey(row) {
  return `${row.retailer}::${row.pageType}::${row.attribute}`;
}

function SkillCsvOnboardingPage() {
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [rows, setRows] = useState(null); // null = no CSV uploaded yet
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());

  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState(null);
  const [approveResult, setApproveResult] = useState(null);
  const [statusByKey, setStatusByKey] = useState(() => new Map());

  const resetForNewUpload = () => {
    setRows(null);
    setSelectedKeys(new Set());
    setPreviewError(null);
    setApproveError(null);
    setApproveResult(null);
    setStatusByKey(new Map());
  };

  const handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    // Allow re-selecting the same filename to re-trigger onChange.
    event.target.value = '';
    if (!file) return;

    resetForNewUpload();
    setFileName(file.name);
    setPreviewing(true);
    try {
      const response = await api.previewSkillsCsv(file);
      const previewRows = Array.isArray(response?.rows) ? response.rows : [];
      setRows(previewRows);
      setSelectedKeys(new Set(
        previewRows.filter((r) => APPROVABLE_TYPES.has(r.type)).map(rowKey),
      ));
    } catch (err) {
      setPreviewError(err.message || 'Failed to preview CSV');
      setRows([]);
    } finally {
      setPreviewing(false);
    }
  };

  const approvableRows = useMemo(
    () => (rows || []).filter((r) => APPROVABLE_TYPES.has(r.type)),
    [rows],
  );

  const toggleRow = (key) => {
    setSelectedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedKeys((current) => (
      current.size === approvableRows.length
        ? new Set()
        : new Set(approvableRows.map(rowKey))
    ));
  };

  const selectedTriples = useMemo(
    () => approvableRows
      .filter((r) => selectedKeys.has(rowKey(r)))
      .map((r) => ({
        retailer: r.retailer,
        pageType: r.pageType,
        attribute: r.attribute,
        // The backend groups config-family siblings under their creator's
        // single Cloud Task using these two fields — see
        // app.api.routers.skills._group_approved_rows_for_enqueue. Without
        // them every row gets its own independent (and potentially racing)
        // task even when the preview already showed one would inherit.
        type: r.type,
        inheritsFromTriple: r.inheritsFromTriple,
      })),
    [approvableRows, selectedKeys],
  );

  const handleApprove = async () => {
    if (selectedTriples.length === 0) return;
    setApproving(true);
    setApproveError(null);
    try {
      const result = await api.approveSkillsCsv(selectedTriples);
      setApproveResult(result);
    } catch (err) {
      setApproveError(err.message || 'Failed to approve skill onboarding');
    } finally {
      setApproving(false);
    }
  };

  // Poll status for the queued rows until every one reaches a terminal state.
  const pollTriples = useMemo(() => {
    const queued = approveResult?.queued;
    return Array.isArray(queued) ? queued : [];
  }, [approveResult]);

  useEffect(() => {
    if (pollTriples.length === 0) return undefined;

    let cancelled = false;
    let intervalId;

    // Checked against the just-fetched map, not React state — state updates
    // are async, so gating on `statusByKey` here would always see the
    // previous tick's data and the interval would never self-clear.
    const isDone = (statusMap) => pollTriples.every((t) => {
      const s = statusMap.get(rowKey(t));
      return s && TERMINAL_STATUSES.has(s.status);
    });

    const poll = async () => {
      try {
        const response = await api.getSkillsCsvStatus(pollTriples);
        if (cancelled) return;
        const statusRows = Array.isArray(response?.rows) ? response.rows : [];
        const nextMap = new Map(statusRows.map((r) => [rowKey(r), r]));
        setStatusByKey(nextMap);
        if (isDone(nextMap) && intervalId) {
          clearInterval(intervalId);
        }
      } catch {
        // Transient polling failure — next tick retries; nothing to surface.
      }
    };

    poll();
    intervalId = setInterval(poll, STATUS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollTriples]);

  const queuedDoneCount = pollTriples.filter((t) => {
    const s = statusByKey.get(rowKey(t));
    return s && TERMINAL_STATUSES.has(s.status);
  }).length;

  return (
    <Box>
      <Typography variant="h5" sx={{ color: 'white', fontWeight: 600, mb: 3 }}>
        Bulk onboard skills from CSV
      </Typography>

      <Card title="1. Upload CSV" gradient sx={{ mb: 3 }}>
        <Typography sx={{ color: '#94a3b8', mb: 2 }}>
          Columns: <code>retailer</code>, <code>page_type</code>, <code>attribute</code>.
          {' '}<code>page_type</code> defaults to <code>detail</code> when omitted.
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadSkillCsvTemplate}
          >
            Download template
          </Button>
          <Button
            startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={previewing}
          >
            {previewing ? 'Validating…' : 'Choose CSV file'}
          </Button>
          {fileName && (
            <Typography sx={{ color: '#e2e8f0' }}>{fileName}</Typography>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={handleFileChange}
          />
        </Stack>
      </Card>

      {previewError && <Alert variant="error" onClose={() => setPreviewError(null)}>{previewError}</Alert>}

      {previewing && <Loading message="Validating and previewing rows…" />}

      {!previewing && rows !== null && (
        <Card title={`2. Review preview (${rows.length} row${rows.length === 1 ? '' : 's'})`} gradient sx={{ mb: 3 }}>
          {rows.length === 0 ? (
            <EmptyState message="No rows found in this CSV" />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={approvableRows.length > 0 && selectedKeys.size === approvableRows.length}
                        indeterminate={selectedKeys.size > 0 && selectedKeys.size < approvableRows.length}
                        onChange={toggleAll}
                        disabled={approvableRows.length === 0 || Boolean(approveResult)}
                      />
                    </TableCell>
                    <TableCell><strong>Row</strong></TableCell>
                    <TableCell><strong>Retailer</strong></TableCell>
                    <TableCell><strong>Page Type</strong></TableCell>
                    <TableCell><strong>Attribute</strong></TableCell>
                    <TableCell><strong>Result</strong></TableCell>
                    <TableCell><strong>Details</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const key = rowKey(row);
                    const approvable = APPROVABLE_TYPES.has(row.type);
                    const statusRow = statusByKey.get(key);
                    return (
                      <TableRow key={`${key}-${row.rowNumber}`} hover>
                        <TableCell padding="checkbox">
                          {approvable && (
                            <Checkbox
                              checked={selectedKeys.has(key)}
                              onChange={() => toggleRow(key)}
                              disabled={Boolean(approveResult)}
                            />
                          )}
                        </TableCell>
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>{row.retailer}</TableCell>
                        <TableCell>{row.pageType}</TableCell>
                        <TableCell>{row.attribute}</TableCell>
                        <TableCell>
                          {statusRow ? (
                            <Chip
                              label={statusRow.status}
                              size="small"
                              sx={{
                                background: TERMINAL_STATUSES.has(statusRow.status)
                                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                  : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                color: 'white',
                                fontWeight: 600,
                              }}
                            />
                          ) : (
                            <TypeChip type={row.type} />
                          )}
                        </TableCell>
                        <TableCell>
                          {row.type === 'will_inherit' && row.skillPath && (
                            <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                              inherits {row.skillPath}
                            </Typography>
                          )}
                          {row.type === 'already_onboarded' && row.skillPath && (
                            <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                              {row.skillPath}
                            </Typography>
                          )}
                          {(row.type === 'error' || row.type === 'invalid' || row.type === 'unhealable') && row.reason && (
                            <Typography sx={{ fontSize: '0.8rem', color: row.type === 'unhealable' ? '#fbbf24' : '#f87171' }}>
                              {row.reason}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Card>
      )}

      {!previewing && rows !== null && rows.length > 0 && (
        <Card title="3. Approve and queue generation" gradient>
          {approveError && <Alert variant="error" onClose={() => setApproveError(null)}>{approveError}</Alert>}

          {!approveResult ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <Button onClick={handleApprove} disabled={approving || selectedTriples.length === 0}>
                {approving ? 'Queueing…' : `Onboard ${selectedTriples.length} selected row${selectedTriples.length === 1 ? '' : 's'}`}
              </Button>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                Only rows checked above (will create / will inherit) can be approved.
              </Typography>
            </Stack>
          ) : (
            <Box>
              <Alert variant="success">
                Queued {approveResult.queued?.length || 0} row{(approveResult.queued?.length || 0) === 1 ? '' : 's'}
                {approveResult.skipped?.length > 0 ? `, skipped ${approveResult.skipped.length}` : ''}
                {approveResult.failedToEnqueue?.length > 0 ? `, ${approveResult.failedToEnqueue.length} failed to enqueue` : ''}.
                {' '}
                <Link to="/skills" style={{ color: '#93c5fd', fontWeight: 600 }}>View skills</Link>
              </Alert>
              {pollTriples.length > 0 && (
                <Typography sx={{ color: '#94a3b8', mt: 1 }}>
                  Generation progress: {queuedDoneCount} / {pollTriples.length} complete
                </Typography>
              )}
              {approveResult.failedToEnqueue?.length > 0 && (
                <Alert variant="warning">
                  {approveResult.failedToEnqueue.length} row(s) could not be enqueued — retry the upload for these rows.
                </Alert>
              )}
            </Box>
          )}
        </Card>
      )}
    </Box>
  );
}

export default SkillCsvOnboardingPage;
